/**
 * Reddit click-ID (rdt_cid) first-party cookie.
 *
 * Reddit's Conversions API (CAPI) attributes conversions to ad clicks using
 * the `rdt_cid` query parameter that Reddit auto-appends to destination URLs
 * when "Auto-append click ID" is enabled on the campaign. We capture that
 * value the first time we see it and persist it to a first-party cookie so
 * we can forward it to our backend (Composer) at signup / upgrade time, even
 * if the user lands on one page and converts on another.
 *
 * Scope constraints:
 * - NOT HttpOnly: both middleware (server) and the signup client call need
 *   to read this value. The cookie contains only a Reddit-issued tracking
 *   identifier; it carries no authentication material.
 * - 30-day Max-Age: matches the attribution window we committed to in
 *   product; Reddit's own click-attribution window is 7 days but enterprise
 *   sales funnels often take longer.
 * - SameSite=Lax: required for the ad-landing case (user clicks a Reddit
 *   ad, lands on our site via top-level navigation). Strict would break
 *   first-party capture.
 */

export const REDDIT_CLICK_ID_COOKIE_NAME = "nrs_rdt_cid";

export const REDDIT_CLICK_ID_COOKIE_MAX_AGE_SEC = 30 * 24 * 60 * 60;

/**
 * Max accepted length for an `rdt_cid` value. Reddit's current click IDs are
 * well under this; we cap aggressively to make cookie-bomb abuse a non-issue.
 */
export const REDDIT_CLICK_ID_MAX_LENGTH = 256;

/**
 * Query parameter that Reddit appends to ad landing URLs when auto-click-ID
 * is enabled in the campaign settings.
 */
export const REDDIT_CLICK_ID_QUERY_PARAM = "rdt_cid";

/**
 * Returns a sanitized click-ID value or null if the input is invalid.
 *
 * Acceptable shape: non-empty string of URL-safe characters (alphanumeric,
 * hyphen, underscore, period, tilde) up to REDDIT_CLICK_ID_MAX_LENGTH after
 * trimming. Anything else is treated as garbage (attacker-controlled query
 * param) and rejected.
 */
export function sanitizeRedditClickId(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.length > REDDIT_CLICK_ID_MAX_LENGTH) return null;
  if (!/^[A-Za-z0-9._~-]+$/.test(trimmed)) return null;
  return trimmed;
}

/**
 * Reads the Reddit click-ID from a raw Cookie header string (as seen by
 * server code that only has access to the raw header). Returns null if the
 * cookie is absent or the stored value fails sanitization.
 */
export function readRedditClickIdFromCookieHeader(
  header: string | null | undefined
): string | null {
  if (!header) return null;
  const match = header.match(
    new RegExp(`(?:^|; )${REDDIT_CLICK_ID_COOKIE_NAME}=([^;]*)`)
  );
  if (!match) return null;
  try {
    return sanitizeRedditClickId(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

/**
 * Client-only reader: returns the stored click-ID (cookie), falling back to
 * the current URL's `rdt_cid` query parameter when present. The fallback
 * ensures that the very first landing (before middleware has set the
 * cookie, e.g. in a test environment) still carries attribution.
 *
 * Returns null when called outside a browser context.
 */
export function getRedditClickIdFromBrowser(): string | null {
  if (typeof document === "undefined") return null;
  const fromCookie = readRedditClickIdFromCookieHeader(document.cookie);
  if (fromCookie) return fromCookie;
  if (typeof window !== "undefined") {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(
        REDDIT_CLICK_ID_QUERY_PARAM
      );
      return sanitizeRedditClickId(fromUrl);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Serializes the Set-Cookie directive used by our middleware. Kept here (not
 * inlined in middleware.ts) so it's unit-testable and the cookie attributes
 * are defined once.
 *
 * `isSecureContext` should be true in production (HTTPS) and false for local
 * HTTP development; Secure cookies are silently dropped by browsers on plain
 * HTTP so we must omit the flag when serving over http://.
 */
export function buildRedditClickIdSetCookie(
  value: string,
  options: { isSecureContext: boolean }
): string {
  const parts = [
    `${REDDIT_CLICK_ID_COOKIE_NAME}=${encodeURIComponent(value)}`,
    `Max-Age=${REDDIT_CLICK_ID_COOKIE_MAX_AGE_SEC}`,
    "Path=/",
    "SameSite=Lax",
  ];
  if (options.isSecureContext) {
    parts.push("Secure");
  }
  return parts.join("; ");
}
